/**
 * API service for image editing.
 *
 * Uploads images directly to R2 via presigned URLs,
 * then sends R2 CDN URLs to the server — no base64 or FormData.
 */

import { apiPost } from "./apiClient";
import { uploadSingleImageToR2, uploadImagesToR2, base64ToBlob } from "./uploadImage";
import { isUrl } from "@/lib/mediaUtils";
import type { EditImageResponse } from "@shared/schemas/api";

export type { EditImageResponse } from "@shared/schemas/api";

interface EditImageParams {
  sourceImage?: string;
  prompt: string;
  aspectRatio?: string;
  resolution?: string;
  modelId?: string;
  quantity?: number;
  maskBlob?: Blob;
  referenceImages?: string[];
}

/** Fetch a URL as a Blob */
async function fetchAsBlob(url: string): Promise<Blob> {
  const response = await fetch(url, { mode: "cors", cache: "no-store" });
  if (!response.ok) throw new Error(`Failed to fetch ${url}: ${response.status}`);
  return response.blob();
}

/**
 * Upload images to R2 first, then call the edit-image API with URLs.
 */
export async function editImage(
  params: EditImageParams
): Promise<EditImageResponse> {
  try {
    // 1. Upload source image to R2
    let imageUrl: string | undefined;
    if (params.sourceImage) {
      const blob = isUrl(params.sourceImage)
        ? await fetchAsBlob(params.sourceImage)
        : base64ToBlob(params.sourceImage);
      imageUrl = await uploadSingleImageToR2(blob);
    }

    // 2. Upload mask to R2
    let maskUrl: string | undefined;
    if (params.maskBlob) {
      maskUrl = await uploadSingleImageToR2(params.maskBlob);
    }

    // 3. Upload reference images to R2
    let referenceImageUrls: string[] | undefined;
    if (params.referenceImages && params.referenceImages.length > 0) {
      const refBlobs = await Promise.all(
        params.referenceImages.map((ref) =>
          isUrl(ref) ? fetchAsBlob(ref) : Promise.resolve(base64ToBlob(ref))
        )
      );
      const uploaded = await uploadImagesToR2(refBlobs);
      referenceImageUrls = uploaded.map((u) => u.publicUrl);
    }

    // 4. Send JSON with URLs to the server
    return await apiPost<EditImageResponse>("/api/edit-image", {
      prompt: params.prompt,
      aspectRatio: params.aspectRatio,
      resolution: params.resolution,
      modelId: params.modelId,
      quantity: params.quantity ?? 1,
      imageUrl,
      maskUrl,
      referenceImageUrls,
    });
  } catch (error) {
    console.error("Error calling edit-image:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Failed to edit image",
      code: (error as Error & { code?: string })?.code,
    };
  }
}
